import React, { useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  Switch,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Flag } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { noOutlineStyle, isWeb } from '@/lib/platform';
import { useAlert } from '@/components/AlertDialog';
import { getUserMessage } from '@/lib/errors';
import {
  useAllFeatureFlags,
  useUpsertFeatureFlag,
  type FeatureFlag,
} from '@/lib/feature-flags';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Editable card for a single feature flag. */
function FlagCard({ flag }: { flag: FeatureFlag }) {
  const colors = useThemeColors();
  const { success: showSuccess, error: showError } = useAlert();
  const upsert = useUpsertFeatureFlag();

  const [enabled, setEnabled] = useState(flag.enabled);
  const [audience, setAudience] = useState<'all' | 'allowlist'>(
    flag.audience === 'allowlist' ? 'allowlist' : 'all',
  );
  const [allowlistText, setAllowlistText] = useState((flag.allowed_user_ids ?? []).join('\n'));

  const parsedIds = allowlistText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const dirty =
    enabled !== flag.enabled ||
    audience !== flag.audience ||
    parsedIds.join(',') !== (flag.allowed_user_ids ?? []).join(',');

  const handleSave = () => {
    if (audience === 'allowlist') {
      const bad = parsedIds.filter((id) => !UUID_RE.test(id));
      if (bad.length > 0) {
        showError('Invalid user IDs', `These are not valid UUIDs:\n${bad.join('\n')}`);
        return;
      }
    }
    upsert.mutate(
      {
        key: flag.key,
        enabled,
        audience,
        allowed_user_ids: audience === 'allowlist' ? parsedIds : [],
        description: flag.description,
      },
      {
        onSuccess: () => showSuccess('Saved', `“${flag.key}” updated.`),
        onError: (err) => showError('Save failed', getUserMessage(err)),
      },
    );
  };

  return (
    <View
      className="rounded-2xl border p-4 mb-3"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
    >
      {/* Key + enable toggle */}
      <View className="flex-row items-center justify-between">
        <Text
          style={{ color: colors.text.primary, fontFamily: 'monospace' }}
          className="text-base font-semibold flex-1"
          numberOfLines={1}
        >
          {flag.key}
        </Text>
        <Switch
          value={enabled}
          onValueChange={setEnabled}
          trackColor={{ false: colors.border.medium, true: colors.primary[500] }}
        />
      </View>

      {flag.description ? (
        <Text style={{ color: colors.text.tertiary }} className="text-sm mt-1" numberOfLines={3}>
          {flag.description}
        </Text>
      ) : null}

      {/* Audience segmented control */}
      <View className="flex-row mt-3 rounded-xl overflow-hidden border" style={{ borderColor: colors.border.light }}>
        {(['all', 'allowlist'] as const).map((aud) => {
          const active = audience === aud;
          return (
            <AnimatedPressable
              key={aud}
              onPress={() => setAudience(aud)}
              className="flex-1 py-2 items-center"
              style={{ backgroundColor: active ? colors.primary[500] : colors.surface.secondary }}
            >
              <Text
                className="text-sm font-medium"
                style={{ color: active ? colors.text.inverse : colors.text.secondary }}
              >
                {aud === 'all' ? 'Everyone' : 'Allowlist'}
              </Text>
            </AnimatedPressable>
          );
        })}
      </View>

      {/* Allowlist editor */}
      {audience === 'allowlist' ? (
        <View className="mt-3">
          <Text style={{ color: colors.text.secondary }} className="text-xs mb-1">
            Allowed user IDs ({parsedIds.length}) — one UUID per line or comma-separated
          </Text>
          <TextInput
            value={allowlistText}
            onChangeText={setAllowlistText}
            multiline
            placeholder="00000000-0000-0000-0000-000000000000"
            placeholderTextColor={colors.text.tertiary}
            autoCapitalize="none"
            autoCorrect={false}
            style={noOutlineStyle({
              color: colors.text.primary,
              backgroundColor: colors.surface.secondary,
              borderColor: colors.border.light,
              minHeight: 72,
              fontFamily: 'monospace',
            })}
            className="rounded-xl border px-3 py-2 text-sm"
          />
        </View>
      ) : null}

      {/* Save */}
      <View className="flex-row justify-end mt-3">
        <AnimatedPressable
          onPress={handleSave}
          disabled={!dirty || upsert.isPending}
          className="px-5 py-2.5 rounded-xl"
          style={{ backgroundColor: dirty ? colors.primary[600] : colors.surface.secondary, opacity: upsert.isPending ? 0.6 : 1 }}
        >
          <Text className="font-semibold" style={{ color: dirty ? colors.text.inverse : colors.text.tertiary }}>
            {upsert.isPending ? 'Saving…' : 'Save'}
          </Text>
        </AnimatedPressable>
      </View>
    </View>
  );
}

export default function FeatureFlagsScreen() {
  const colors = useThemeColors();
  const { data: flags, isLoading, isError } = useAllFeatureFlags();

  return (
    <AppLayout
      title="Feature Flags"
      breadcrumbs={[
        { label: 'Admin', href: '/(admin)/admin-dashboard' },
        { label: 'Feature Flags' },
      ]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={isWeb ? [] : ['top', 'bottom']}>
        <View className="px-1 pt-2 pb-3">
          <View className="flex-row items-center">
            <Flag size={18} color={colors.text.primary} />
            <Text style={{ color: colors.text.primary }} className="text-xl font-bold ml-2">
              Feature Flags
            </Text>
          </View>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
            Kill-switch a surface (toggle off) or gate it to specific testers (Allowlist). Changes propagate within a minute.
          </Text>
        </View>

        {isLoading ? (
          <View className="flex-1 items-center justify-center py-12">
            <ActivityIndicator color={colors.primary[600]} />
          </View>
        ) : isError ? (
          <Text style={{ color: colors.traffic.red }} className="text-sm px-1">
            Couldn’t load feature flags.
          </Text>
        ) : !flags || flags.length === 0 ? (
          <Text style={{ color: colors.text.tertiary }} className="text-sm px-1">
            No flags yet. Add one via a migration (`public.feature_flags`).
          </Text>
        ) : (
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
            {flags.map((flag) => (
              <FlagCard key={flag.key} flag={flag} />
            ))}
          </ScrollView>
        )}
      </SafeAreaView>
    </AppLayout>
  );
}
